import React, { useCallback, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import PageWrapper from "../../components/PageWrapper";
import ExcelUpload from "../../api/excelUpload";
import {
    ComparisonTask,
    ComparisonIntermediate,
    PieceComparisonResponse,
    ProjectTasksComparison,
    HourlyCostCodesComparison,
} from "../../interfaces/ExcelUploadInterface";
import DollarUtil from "../../util/DollarUtil";
import {
    UploadCloud,
    FileInput,       // page header tile (sidebar match)
    GitCompare,      // action dock icon
    UserCheck,       // "Has claims" marker
    Eye,
    EyeOff,
    Shield,          // "Kept" pill + warn banner
    AlertTriangle,
    Info,
    Check,
    CheckCircle,
    RefreshCw,
    ChevronDown,
    X,
    File,
    Folder,
    Layers,
    List,
    Clock,
    Upload,
    Briefcase,
} from "lucide-react";
import "./PieceImport.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const CSV_ACCEPT = {
    "text/csv":         [".csv"],
    "application/csv":  [".csv"],
    "text/plain":       [".csv"],
};

const DASH = "—";

// ─── Types ────────────────────────────────────────────────────────────────────

type PageState = "empty" | "loading" | "diff" | "error";

interface ToastItem {
    id: number;
    kind: "ok" | "err";
    msg: string;
    out?: boolean;
}

interface RollResult {
    orig: number;
    rem: number;
    neu: number;
    tasks: number;
    changed: boolean;
}

// ─── Rollup math (mirrors roll() in taskimport.js) ────────────────────────────
//
// Amounts:
//  originalTaskAmount  — task's original value when it already existed
//  remainingTaskAmount — what is still unclaimed
//  originalAmount      — Task base field (also used as fallback)
//  newAmount           — the incoming value for new/updated tasks

function currentAmt(t: ComparisonTask): number | null {
    if (t.status === "new")     return t.newAmount ?? null;
    if (t.status === "updated") return t.newAmount ?? null;
    if (t.status === "claimed") return t.remainingTaskAmount ?? t.originalAmount;
    if (t.status === "removed") return null;
    return t.originalAmount; // unchanged
}

function roll(node: ComparisonIntermediate): RollResult {
    let orig = 0, rem = 0, neu = 0, tasks = 0, changed = false;
    (node.tasks ?? []).forEach((t) => {
        tasks++;
        if (t.status !== "unchanged") changed = true;
        const o = t.originalTaskAmount != null ? t.originalTaskAmount
                : (t.status === "new" ? 0 : t.originalAmount);
        const r = t.remainingTaskAmount != null ? t.remainingTaskAmount
                : (t.status === "new" ? 0 : t.originalAmount);
        const n = t.status === "removed" ? 0 : (currentAmt(t) ?? 0);
        orig += o; rem += r; neu += n;
    });
    (node.intermediate ?? []).forEach((c) => {
        const s = roll(c);
        orig += s.orig; rem += s.rem; neu += s.neu; tasks += s.tasks;
        if (s.changed || (c.status && c.status !== "unchanged")) changed = true;
    });
    if (node.status && node.status !== "unchanged") changed = true;
    return { orig, rem, neu, tasks, changed };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtAmt(n: number | null | undefined): React.ReactNode {
    if (n == null) return <span className="timp-dash">{DASH}</span>;
    return DollarUtil.formatDollar(n);
}

function fmtFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** status → CSS modifier (used for pills and row accents) */
function stClass(status: string): string {
    switch (status) {
        case "new":       return "added";
        case "removed":   return "removed";
        case "updated":   return "updated";
        case "unchanged": return "unchanged";
        case "claimed":   return "unchanged"; // fallback — handled specially before reaching this
        default:          return "unchanged";
    }
}

/** status → display label */
function stLabel(status: string): string {
    switch (status) {
        case "new":       return "Added";
        case "removed":   return "Removed";
        case "updated":   return "Updated";
        case "unchanged": return "Unchanged";
        case "claimed":   return "Unchanged";
        default:          return "Unchanged";
    }
}

/** True when a removed task has partial employee claims remaining.
 *
 *  Per the spec: status:'removed' + remainingTaskAmount < originalTaskAmount
 *  means the employee's claimed share is kept but the remaining balance is
 *  zeroed so no further claims can be made.
 */
function isPartialClaim(t: ComparisonTask): boolean {
    return (
        t.status === "removed" &&
        t.remainingTaskAmount != null &&
        t.originalTaskAmount  != null &&
        t.remainingTaskAmount < t.originalTaskAmount
    );
}

/** Escape server/user text before it goes into a toast (toasts render as HTML). */
function escapeHtml(text: string): string {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/** Collect every node key in the tree (depth-first), for Expand/Collapse all. */
function collectAllNodeKeys(
    nodes: ComparisonIntermediate[],
    prefix: string,
): string[] {
    const keys: string[] = [];
    nodes.forEach((n) => {
        const key = prefix + "/" + n.name;
        keys.push(key);
        if (n.intermediate?.length) {
            keys.push(...collectAllNodeKeys(n.intermediate, key));
        }
    });
    return keys;
}

// ─── Component ────────────────────────────────────────────────────────────────

const PieceImport: React.FC = () => {
    // ── Page state machine ────────────────────────────────────────────────
    const [pageState,          setPageState]          = useState<PageState>("empty");
    const [selectedFile,       setSelectedFile]       = useState<File | null>(null);
    const [comparisonResponse, setComparisonResponse] = useState<PieceComparisonResponse | null>(null);
    const [errorMessage,       setErrorMessage]       = useState<string | null>(null);

    // ── UI state ──────────────────────────────────────────────────────────
    const [hideUnchanged,  setHideUnchanged]  = useState(false);
    const [expandedNodes,  setExpandedNodes]  = useState<Set<string>>(new Set());
    const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());
    const [confirmOpen,    setConfirmOpen]    = useState(false);
    const [successOpen,    setSuccessOpen]    = useState(false);
    const [applyLoading,   setApplyLoading]   = useState(false);

    // ── Toasts ────────────────────────────────────────────────────────────
    const [toasts,      setToasts]      = useState<ToastItem[]>([]);
    const toastIdRef = useRef(0);

    const showToast = useCallback((kind: "ok" | "err", msg: string) => {
        const id = ++toastIdRef.current;
        setToasts((prev) => [...prev, { id, kind, msg }]);
        setTimeout(() => {
            setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, out: true } : t)));
            setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 220);
        }, 3600);
    }, []);

    // ── Drop / compare ────────────────────────────────────────────────────
    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (!acceptedFiles.length) return;
        const file = acceptedFiles[0];
        setSelectedFile(file);
        setComparisonResponse(null);
        setErrorMessage(null);
        setConfirmOpen(false);
        setSuccessOpen(false);
        setPageState("loading");
        try {
            const response = await ExcelUpload.uploadPieceCsvComparison(file);
            if (!response) {
                setPageState("error");
                return;
            }
            // Initialise expanded state: top-level nodes open, deeper collapsed.
            const intermediates = response.projectTasksComparison.intermediates ?? [];
            const depth1Keys = new Set(intermediates.map((n) => "/" + n.name));
            setExpandedNodes(depth1Keys);
            // All cost-code phases start open.
            const phases = response.hourlyCostCodesComparison?.hourlyCostCodes ?? [];
            setExpandedPhases(new Set(phases.map((p) => p.phase)));
            setHideUnchanged(false);
            setComparisonResponse(response);
            setPageState("diff");
        } catch (err: any) {
            setErrorMessage(typeof err?.message === "string" ? err.message : null);
            setPageState("error");
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive, open: openFilePicker } = useDropzone({
        onDrop,
        accept:   CSV_ACCEPT,
        multiple: false,
    });

    // ── Reset ─────────────────────────────────────────────────────────────
    const handleReset = useCallback(() => {
        setPageState("empty");
        setSelectedFile(null);
        setComparisonResponse(null);
        setErrorMessage(null);
        setConfirmOpen(false);
        setSuccessOpen(false);
        setHideUnchanged(false);
        setExpandedNodes(new Set());
        setExpandedPhases(new Set());
    }, []);

    // ── Retry (re-submit the same file) ───────────────────────────────────
    const handleRetry = useCallback(() => {
        if (selectedFile) onDrop([selectedFile]);
        else handleReset();
    }, [selectedFile, onDrop, handleReset]);

    // ── Apply changes ─────────────────────────────────────────────────────
    const handleApply = useCallback(async () => {
        if (!selectedFile) return;
        setConfirmOpen(false);
        setApplyLoading(true);
        try {
            const result = await ExcelUpload.uploadPieceCsvApplyChanges(selectedFile);
            if (!result) {
                showToast("err", "Apply failed. Please try again.");
                return;
            }
            setSuccessOpen(true);
        } catch (err: any) {
            const detail = typeof err?.message === "string" ? err.message : "";
            showToast("err", detail ? `<b>Apply failed</b> ${escapeHtml(detail)}` : "Apply failed. Please try again.");
        } finally {
            setApplyLoading(false);
        }
    }, [selectedFile, showToast]);

    const handleFinishSuccess = useCallback(() => {
        const name = comparisonResponse?.projectName ?? "";
        setSuccessOpen(false);
        showToast("ok", `<b>Import applied</b>${name ? ` to ${name}` : ""}`);
        handleReset();
    }, [comparisonResponse, showToast, handleReset]);

    // ── Tree expand / collapse ────────────────────────────────────────────
    const handleExpandAll = useCallback((open: boolean) => {
        if (!comparisonResponse) return;
        if (open) {
            const allKeys = collectAllNodeKeys(
                comparisonResponse.projectTasksComparison.intermediates ?? [], "",
            );
            setExpandedNodes(new Set(allKeys));
        } else {
            setExpandedNodes(new Set());
        }
    }, [comparisonResponse]);

    const toggleNode = (key: string) => {
        setExpandedNodes((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const togglePhase = (phase: string) => {
        setExpandedPhases((prev) => {
            const next = new Set(prev);
            next.has(phase) ? next.delete(phase) : next.add(phase);
            return next;
        });
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDER HELPERS
    // ═══════════════════════════════════════════════════════════════════════

    // ── Stepper ───────────────────────────────────────────────────────────
    const renderStepper = (activeStep: number, isLoading = false) => {
        const labels = ["Drop file",     "Review comparison", "Apply"];
        const descs  = ["CSV export",    "What would change", "Write changes"];
        return (
            <div className="timp-stepper">
                {labels.map((label, i) => {
                    const n      = i + 1;
                    const isDone = n < activeStep;
                    const isOn   = n === activeStep;
                    return (
                        <React.Fragment key={n}>
                            {i > 0 && (
                                <span className={`timp-step-line${i < activeStep ? " done" : ""}`} />
                            )}
                            <div className={`timp-step${isDone ? " done" : ""}${isOn ? " on" : ""}`}>
                                <span className="timp-s-num">
                                    {isDone ? (
                                        <Check size={14} />
                                    ) : (isLoading && n === 1) ? (
                                        <span className="timp-spin" style={{ width: 14, height: 14 }} />
                                    ) : n}
                                </span>
                                <span className="timp-s-lab">
                                    <span className="timp-s-t">{label}</span>
                                    <span className="timp-s-d">{descs[i]}</span>
                                </span>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    // ── State A: Empty dropzone ───────────────────────────────────────────
    const renderEmpty = () => (
        <>
            {renderStepper(1)}
            <div className="timp-dz-wrap">
                <div
                    {...getRootProps({
                        className: `timp-dropzone${isDragActive ? " drag" : ""}`,
                    })}
                >
                    <input {...getInputProps()} />
                    <div className="timp-dz-ic">
                        <UploadCloud size={30} />
                    </div>
                    <h2>Drop the piece-work CSV to compare</h2>
                    <div className="timp-dz-sub">
                        The file identifies its project automatically. We compare it against the
                        project&apos;s existing tasks and show you exactly what would change before
                        anything is applied.
                    </div>
                    <button
                        type="button"
                        className="timp-dz-btn"
                        onClick={(e) => { e.stopPropagation(); openFilePicker(); }}
                    >
                        <Folder size={16} />
                        Browse files
                    </button>
                </div>
                <div className="timp-dz-hint">
                    <Info size={14} />
                    Accepts a single <code>.csv</code> export — no project picker needed.
                </div>
            </div>
        </>
    );

    // ── State B: Loading ──────────────────────────────────────────────────
    const renderLoading = () => (
        <>
            {renderStepper(1, true)}
            <div className="timp-state-card">
                <div className="timp-sc-ic load">
                    <span className="timp-spin" style={{ width: 30, height: 30, borderWidth: 3 }} />
                </div>
                <h2>Comparing against existing tasks…</h2>
                <p>
                    Reading the CSV, matching the project, and diffing every task and
                    intermediate.
                </p>
                {selectedFile && (
                    <div className="timp-sc-file">
                        <File size={13} />
                        {selectedFile.name} · {fmtFileSize(selectedFile.size)}
                    </div>
                )}
                <div className="timp-load-bar"><span /></div>
            </div>
        </>
    );

    // ── State D: Error ────────────────────────────────────────────────────
    const renderError = () => (
        <>
            {renderStepper(1)}
            <div className="timp-state-card">
                <div className="timp-sc-ic err">
                    <AlertTriangle size={30} />
                </div>
                <h2>We couldn&apos;t compare that file</h2>
                <p>
                    {errorMessage
                        ? "The import was rejected before anything changed. Correct the issue below in the CSV (or in the project's setup), then try again."
                        : "The CSV couldn't be matched to a project, or a required column is missing. Check that you exported the piece-work CSV from the source system, then try again."}
                </p>
                {errorMessage && (
                    <div className="timp-sc-err-detail">
                        <span className="timp-sc-err-label">Details</span>
                        <span className="timp-sc-err-msg">{errorMessage}</span>
                    </div>
                )}
                {selectedFile && (
                    <div className="timp-sc-file">
                        <File size={13} />
                        {selectedFile.name}
                    </div>
                )}
                <div className="timp-sc-actions">
                    <button className="timp-btn" type="button" onClick={handleReset}>
                        <RefreshCw size={16} />
                        Choose a different file
                    </button>
                    <button className="timp-btn primary" type="button" onClick={handleRetry}>
                        <RefreshCw size={16} />
                        Retry
                    </button>
                </div>
            </div>
        </>
    );

    // ── Task row (diff view) ──────────────────────────────────────────────
    //
    // Claim-protection rendering rules (per spec):
    //   status:'claimed'     → fully-claimed, API keeps it; blue "Kept" pill +
    //                          "Has claims" marker, no strikethrough, never hidden.
    //   status:'removed' + remainingTaskAmount < originalTaskAmount
    //                        → partial claim; removed treatment + amber
    //                          "Claims kept · remaining balance zeroed" marker.
    //   status:'removed' (remaining == original or undefined)
    //                        → plain removal; red "Removed" pill + strikethrough.
    const renderTaskRow = (t: ComparisonTask, key: string): React.ReactNode => {
        const isProtected = t.status === "claimed";
        const isPartial   = isPartialClaim(t);

        // Skip hidden unchanged rows (protected tasks are never hidden).
        if (hideUnchanged && t.status === "unchanged" && !isProtected) return null;

        // ── Status pill ──────────────────────────────────────────────────
        const pillEl: React.ReactNode = isProtected ? (
            <span className="timp-st-pill kept">
                <Shield size={12} />
                Kept
            </span>
        ) : (
            <span className={`timp-st-pill ${stClass(t.status)}`}>
                <span className="timp-dot" />
                {stLabel(t.status)}
            </span>
        );

        // ── Claim markers ────────────────────────────────────────────────
        const claimMarkEl: React.ReactNode = isProtected ? (
            <span className="timp-claim-mark">
                <UserCheck size={12} />
                Has claims
            </span>
        ) : null;

        const partialMarkEl: React.ReactNode = isPartial ? (
            <span className="timp-partial-mark">
                Claims kept · remaining balance zeroed
            </span>
        ) : null;

        // ── Amount columns ───────────────────────────────────────────────
        let amtEl: React.ReactNode;
        if (isProtected) {
            // Kept task: show its current (remaining) value in the New column.
            const keepAmt = t.remainingTaskAmount ?? t.originalAmount;
            amtEl = (
                <div className="timp-amt3">
                    <span className="timp-a-col orig">{fmtAmt(null)}</span>
                    <span className="timp-a-col rem">{fmtAmt(null)}</span>
                    <span className="timp-a-col new">{fmtAmt(keepAmt)}</span>
                </div>
            );
        } else if (t.status === "updated") {
            amtEl = (
                <div className="timp-amt3">
                    <span className="timp-a-col orig">{fmtAmt(t.originalTaskAmount)}</span>
                    <span className="timp-a-col rem">{fmtAmt(t.remainingTaskAmount)}</span>
                    <span className="timp-a-col new">{fmtAmt(t.newAmount)}</span>
                </div>
            );
        } else if (t.status === "removed") {
            amtEl = (
                <div className="timp-amt3">
                    <span className="timp-a-col orig">{fmtAmt(t.originalTaskAmount)}</span>
                    <span className="timp-a-col rem">{fmtAmt(t.remainingTaskAmount)}</span>
                    <span className="timp-a-col new">{fmtAmt(null)}</span>
                </div>
            );
        } else {
            // new, unchanged → single amount in the New column.
            amtEl = (
                <div className="timp-amt3">
                    <span className="timp-a-col orig">{fmtAmt(null)}</span>
                    <span className="timp-a-col rem">{fmtAmt(null)}</span>
                    <span className="timp-a-col new">{fmtAmt(currentAmt(t))}</span>
                </div>
            );
        }

        const rowCls = [
            "timp-task-row",
            `st-${isProtected ? "unchanged" : stClass(t.status)}`,
            isProtected ? "protected" : "",
        ].filter(Boolean).join(" ");

        return (
            <div key={key} className={rowCls}>
                <div className="timp-task-line no-exp">
                    <div className="timp-t-namewrap">
                        <div className="timp-t-tags">
                            {pillEl}
                            {claimMarkEl}
                            {partialMarkEl}
                        </div>
                        <div className="timp-t-name">
                            {t.name}
                            {t.description && (
                                <div className="timp-t-desc">{t.description}</div>
                            )}
                        </div>
                    </div>
                    <div className="timp-t-mid">{amtEl}</div>
                </div>
            </div>
        );
    };

    // ── Recursive intermediate node ───────────────────────────────────────
    const renderNode = (
        node:       ComparisonIntermediate,
        depth:      number,
        pathPrefix: string,
    ): React.ReactNode => {
        const key    = pathPrefix + "/" + node.name;
        const isOpen = expandedNodes.has(key);
        const s      = roll(node);

        const isRemoved       = node.status === "removed";
        const isAdded         = node.status === "new";
        // A removed intermediate that the API won't actually remove (has claims under it).
        const isProtectedNode = isRemoved && node.safeToRemove === false;

        // ── Change-warn marker ───────────────────────────────────────────
        const showWarn = s.changed && !isRemoved && !isAdded;

        // ── Status chip ──────────────────────────────────────────────────
        let statusChipEl: React.ReactNode = null;
        if (isProtectedNode) {
            statusChipEl = (
                <span className="timp-st-pill kept">
                    <Shield size={12} />
                    Kept
                </span>
            );
        } else if (isRemoved) {
            statusChipEl = node.safeToRemove ? (
                <span className="timp-safe-chip">Safe to remove</span>
            ) : (
                <span className="timp-st-pill removed">
                    <span className="timp-dot" />
                    Removed
                </span>
            );
        } else if (isAdded) {
            statusChipEl = (
                <span className="timp-st-pill added">
                    <span className="timp-dot" />
                    Added
                </span>
            );
        }

        // ── Rollup amounts ───────────────────────────────────────────────
        // Depth 1: full three-column Total Original | Remaining | New.
        // Depth > 1: task count + single New total.
        const rollupEl: React.ReactNode = depth === 1 ? (
            <div className="timp-amt3">
                <span className="timp-a-col orig">{fmtAmt(s.orig)}</span>
                <span className="timp-a-col rem">{fmtAmt(s.rem)}</span>
                <span className="timp-a-col new">{fmtAmt(s.neu)}</span>
            </div>
        ) : (
            <>
                <span className="timp-n-count">
                    <b>{s.tasks}</b> task{s.tasks === 1 ? "" : "s"}
                </span>
                <div className="timp-amt3">
                    <span className="timp-a-col new neu">{fmtAmt(s.neu)}</span>
                </div>
            </>
        );

        const nodeCls = [
            "timp-node",
            isProtectedNode ? "protected-node" : (isRemoved ? "st-removed" : isAdded ? "st-added" : ""),
            isOpen ? "" : "collapsed",
        ].filter(Boolean).join(" ");

        return (
            <div key={key} className={nodeCls} data-depth={depth}>
                <div className="timp-node-head" onClick={() => toggleNode(key)}>
                    <ChevronDown className="caret" size={16} />
                    <span className="timp-n-ic"><Layers size={15} /></span>
                    <span className="timp-n-name">{node.name}</span>
                    {showWarn && (
                        <span className="timp-n-warn" title="Contains changes">
                            <AlertTriangle size={16} />
                        </span>
                    )}
                    {statusChipEl}
                    <div className="timp-n-rollup">{rollupEl}</div>
                </div>
                <div className="timp-node-kids">
                    {(node.tasks ?? []).map((t, i) =>
                        renderTaskRow(t, `${key}/task/${i}`)
                    )}
                    {(node.intermediate ?? []).map((c) =>
                        renderNode(c, depth + 1, key)
                    )}
                </div>
            </div>
        );
    };

    // ── Count strip ───────────────────────────────────────────────────────
    const renderCountStrip = (sum: ProjectTasksComparison["summary"]) => (
        <div className="timp-count-strip">
            <div className="timp-count-cell claimed">
                <div className="timp-cc-lab"><span className="timp-dot" />Claimed</div>
                <div className="timp-cc-val">{sum.claimed}</div>
                <div className="timp-cc-sub">existing claimed tasks</div>
            </div>
            <div className="timp-count-cell added">
                <div className="timp-cc-lab"><span className="timp-dot" />Additions</div>
                <div className="timp-cc-val">{sum.additions}</div>
                <div className="timp-cc-sub">new tasks</div>
            </div>
            <div className="timp-count-cell removed">
                <div className="timp-cc-lab"><span className="timp-dot" />Removals</div>
                <div className="timp-cc-val">{sum.removals}</div>
                <div className="timp-cc-sub">tasks removed</div>
            </div>
            <div className="timp-count-cell updated">
                <div className="timp-cc-lab"><span className="timp-dot" />Updates</div>
                <div className="timp-cc-val">{sum.updates}</div>
                <div className="timp-cc-sub">amount changed</div>
            </div>
            <div className="timp-count-cell unchanged">
                <div className="timp-cc-lab"><span className="timp-dot" />Unchanged</div>
                <div className="timp-cc-val">{sum.unchanged}</div>
                <div className="timp-cc-sub">no change</div>
            </div>
            <div className="timp-count-cell">
                <div className="timp-cc-lab">Intermediates</div>
                <div className="timp-cc-inter">
                    <span className="up">+{sum.intermediatesAdded}</span>
                    <span className="dn">−{sum.intermediatesRemoved}</span>
                </div>
                <div className="timp-cc-sub">added · removed</div>
            </div>
        </div>
    );

    // ── Warnings ──────────────────────────────────────────────────────────
    const renderWarnings = (pt: ProjectTasksComparison) => {
        const hasUnsafe = pt.claimedTasksCount > 0 && pt.summary.removals > 0;
        const hasSafe   = (pt.intermediates ?? []).some((n) => n.safeToRemove);
        return (
            <>
                {hasUnsafe && (
                    <div className="timp-warn-banner">
                        <Shield size={17} />
                        <div>
                            <b>This file removes tasks that have employee claims.</b> Claimed amounts
                            are never erased: fully-claimed tasks are kept, partially-claimed tasks keep
                            the employee&apos;s claim but their remaining balance is zeroed so no further
                            claims can be made, and intermediates with claims underneath are kept.
                        </div>
                    </div>
                )}
                {hasSafe && (
                    <div className="timp-preview-line">
                        <Info size={16} />
                        <div>
                            This file will remove existing intermediates that are{" "}
                            <b>safe to remove</b> (no claimed tasks underneath).
                        </div>
                    </div>
                )}
            </>
        );
    };

    // ── Legend ────────────────────────────────────────────────────────────
    const renderLegend = () => (
        <div className="timp-legend">
            <span className="timp-lg-lab">Legend</span>
            <span className="timp-lg-item added">
                <span className="timp-dot" />Added
            </span>
            <span className="timp-lg-item removed">
                <span className="timp-dot" />Removed
            </span>
            <span className="timp-lg-item updated">
                <span className="timp-dot" />Updated
            </span>
            <span className="timp-lg-item unchanged">
                <span className="timp-dot" />Unchanged
            </span>
            <span className="timp-lg-item claimed">
                <UserCheck size={13} />Has claims
            </span>
            <span className="timp-lg-spacer" />
            <button
                type="button"
                className={`timp-lg-toggle${hideUnchanged ? " on" : ""}`}
                onClick={() => setHideUnchanged((v) => !v)}
            >
                {hideUnchanged ? <Eye size={14} /> : <EyeOff size={14} />}
                <span>{hideUnchanged ? "Show unchanged" : "Hide unchanged"}</span>
            </button>
        </div>
    );

    // ── Tasks & Piece Work card ───────────────────────────────────────────
    const renderTasksCard = (pt: ProjectTasksComparison) => {
        const { summary: sum } = pt;
        const totalTasks = sum.additions + sum.removals + sum.updates + sum.unchanged + sum.claimed;
        return (
            <div className="timp-bd-card">
                <div className="timp-bd-head static">
                    <span className="timp-bh-ic"><List size={18} /></span>
                    <div>
                        <h3>Tasks &amp; Piece Work</h3>
                        <div className="timp-bd-sub">Lots &amp; intermediates — expand to inspect</div>
                    </div>
                    <div className="timp-bh-right">
                        <button
                            className="timp-btn sm"
                            type="button"
                            onClick={() => handleExpandAll(true)}
                        >
                            Expand all
                        </button>
                        <button
                            className="timp-btn sm"
                            type="button"
                            onClick={() => handleExpandAll(false)}
                        >
                            Collapse all
                        </button>
                    </div>
                </div>
                <div className="timp-bd-body">
                    <div className="timp-sec-head">
                        <span className="timp-sh-t">Tasks</span>
                        <span className="timp-sh-count">{totalTasks}</span>
                        <div className="timp-sh-amts">
                            <span className="c">Original</span>
                            <span className="c">Remaining</span>
                            <span className="c">New</span>
                        </div>
                    </div>
                    <div className="timp-tree">
                        {(pt.tasks ?? []).map((t, i) =>
                            renderTaskRow(t, `top/task/${i}`)
                        )}
                        {(pt.intermediates ?? []).map((n) =>
                            renderNode(n, 1, "")
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ── Hourly Cost Codes card (optional) ─────────────────────────────────
    const renderCostCodesCard = (cc: HourlyCostCodesComparison) => (
        <div className="timp-bd-card">
            <div className="timp-bd-head">
                <span className="timp-bh-ic"><Clock size={18} /></span>
                <div>
                    <h3>Hourly Cost Codes</h3>
                    <div className="timp-bd-sub">Phase-grouped cost-code changes carried by this file</div>
                </div>
                <div className="timp-bh-right">
                    <div className="timp-bh-metric">
                        <div className="m-val" style={{ color: "var(--s-active)" }}>
                            +{cc.summary.additions}
                        </div>
                        <div className="m-lab">Added</div>
                    </div>
                    <div className="timp-bh-metric">
                        <div className="m-val" style={{ color: "var(--s-danger)" }}>
                            −{cc.summary.removals}
                        </div>
                        <div className="m-lab">Removed</div>
                    </div>
                </div>
            </div>
            <div className="timp-bd-body">
                <div className="timp-cc-summary">
                    <span className="timp-ccs-item add">
                        <span className="timp-dot" />Additions <b>{cc.summary.additions}</b>
                    </span>
                    <span className="timp-ccs-item rem">
                        <span className="timp-dot" />Removals <b>{cc.summary.removals}</b>
                    </span>
                    <span className="timp-ccs-item unch">
                        <span className="timp-dot" />Unchanged <b>{cc.summary.unchanged}</b>
                    </span>
                </div>
                {cc.hourlyCostCodes.map((ph) => {
                    const isPhOpen = expandedPhases.has(ph.phase);
                    const changes  = ph.costCodes.filter((c) => c.status !== "unchanged").length;
                    return (
                        <div key={ph.phase} className={`timp-phase${isPhOpen ? " open" : ""}`}>
                            <div
                                className="timp-phase-head"
                                onClick={() => togglePhase(ph.phase)}
                            >
                                <ChevronDown className="caret" size={16} />
                                <span className="timp-ph-ic"><Clock size={16} /></span>
                                <span className="timp-p-name">{ph.phase}</span>
                                <span className="timp-p-count">
                                    {changes} change{changes !== 1 ? "s" : ""} · {ph.costCodes.length} codes
                                </span>
                            </div>
                            <div className="timp-phase-body">
                                {ph.costCodes.map((code) => {
                                    if (hideUnchanged && code.status === "unchanged") return null;
                                    const sc = stClass(code.status);
                                    return (
                                        <div
                                            key={code.altAccountCodeID}
                                            className={`timp-code-row st-${sc}`}
                                        >
                                            <div className="timp-cr-mainwrap">
                                                <div className="timp-cr-idname">
                                                    <span className="timp-cr-id">
                                                        {code.altAccountCodeID}
                                                    </span>
                                                    <span className="timp-cr-name">{code.name}</span>
                                                </div>
                                                {code.description && (
                                                    <div className="timp-cr-desc">{code.description}</div>
                                                )}
                                            </div>
                                            <span className="timp-cr-st">
                                                <span className={`timp-st-pill ${sc}`}>
                                                    <span className="timp-dot" />
                                                    {stLabel(code.status)}
                                                </span>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // ── Sticky action dock ────────────────────────────────────────────────
    const renderActionDock = (sum: ProjectTasksComparison["summary"]) => (
        <div className="timp-action-dock">
            <div className="timp-ad-summary">
                <GitCompare size={17} />
                <span>Applying will</span>
                <div className="timp-ad-chips">
                    <span className="timp-ad-chip added">
                        <span className="timp-dot" />+{sum.additions} add
                    </span>
                    <span className="timp-ad-chip removed">
                        <span className="timp-dot" />−{sum.removals} remove
                    </span>
                    <span className="timp-ad-chip updated">
                        <span className="timp-dot" />~{sum.updates} update
                    </span>
                </div>
            </div>
            <div className="timp-ad-spacer" />
            <button className="timp-btn" type="button" onClick={handleReset}>
                <RefreshCw size={16} />
                Choose a different file
            </button>
            <button
                className="timp-btn primary"
                type="button"
                onClick={() => setConfirmOpen(true)}
            >
                <Upload size={16} />
                Upload &amp; apply
            </button>
        </div>
    );

    // ── State C: Diff view ────────────────────────────────────────────────
    const renderDiff = () => {
        if (!comparisonResponse || !selectedFile) return null;
        const c   = comparisonResponse;
        const pt  = c.projectTasksComparison;
        const sum = pt.summary;
        return (
            <>
                {renderStepper(2)}

                {/* Preview banner */}
                <div className="timp-preview-banner">
                    <span className="timp-pb-ic"><Eye size={17} /></span>
                    <div className="timp-pb-main">
                        <div className="timp-pb-t">
                            Preview only — nothing has been applied yet
                        </div>
                        <div className="timp-pb-s">
                            Review the comparison below, then confirm with Upload to write these
                            changes.
                        </div>
                    </div>
                    <span className="timp-pb-file">
                        <File size={13} />
                        {selectedFile.name}
                    </span>
                </div>

                {/* Job identity header */}
                <div className="timp-job-head">
                    <span className="timp-jh-ic"><Briefcase size={22} /></span>
                    <div className="timp-jh-main">
                        {c.altProjectID != null && (
                            <div className="timp-jh-id">
                                <span className="timp-code">Job {c.altProjectID}</span>
                            </div>
                        )}
                        {c.projectName != null && (
                            <h2 className="timp-h2">{c.projectName}</h2>
                        )}
                    </div>
                    <span className="timp-jh-tag">
                        <CheckCircle size={12} />
                        Matched existing project
                    </span>
                </div>

                {/* Count strip */}
                {renderCountStrip(sum)}

                {/* Warnings */}
                {renderWarnings(pt)}

                {/* Legend */}
                {renderLegend()}

                {/* Tasks & Piece Work card */}
                {renderTasksCard(pt)}

                {/* Hourly Cost Codes card (optional) */}
                {c.hourlyCostCodesComparison &&
                    renderCostCodesCard(c.hourlyCostCodesComparison)}

                {/* Sticky action dock */}
                {renderActionDock(sum)}
            </>
        );
    };

    // ── State E: Confirm modal ────────────────────────────────────────────
    const renderConfirmModal = () => {
        if (!comparisonResponse) return null;
        const sum     = comparisonResponse.projectTasksComparison.summary;
        const claimed = comparisonResponse.projectTasksComparison.claimedTasksCount;
        return (
            <div
                className={`timp-modal-bg${confirmOpen ? " open" : ""}`}
                onClick={() => setConfirmOpen(false)}
            >
                <div className="timp-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="timp-modal-head">
                        <span className="timp-mi"><Upload size={21} /></span>
                        <div>
                            <h3>Apply changes?</h3>
                            <div className="timp-modal-ms">
                                These changes will be written to the project immediately.
                            </div>
                        </div>
                        <button
                            className="timp-modal-x"
                            type="button"
                            onClick={() => setConfirmOpen(false)}
                        >
                            <X size={16} />
                        </button>
                    </div>
                    <div className="timp-modal-body">
                        <div className="timp-confirm-counts">
                            <div className="timp-cc-stat added">
                                <div className="v">{sum.additions}</div>
                                <div className="l">Added</div>
                            </div>
                            <div className="timp-cc-stat removed">
                                <div className="v">{sum.removals}</div>
                                <div className="l">Removed</div>
                            </div>
                            <div className="timp-cc-stat updated">
                                <div className="v">{sum.updates}</div>
                                <div className="l">Updated</div>
                            </div>
                        </div>
                        <div className={`timp-confirm-note${claimed > 0 ? " amber" : ""}`}>
                            {claimed > 0 ? (
                                <>
                                    <Shield size={16} style={{ color: "var(--accent)" }} />
                                    <span>
                                        <b>
                                            {claimed} claimed task{claimed !== 1 ? "s" : ""}
                                        </b>{" "}
                                        the file tried to remove will be <b>kept</b> — claimed
                                        items, and intermediates with claims underneath, are never
                                        removed. All other changes apply.
                                    </span>
                                </>
                            ) : (
                                <>
                                    <Info size={16} />
                                    <span>
                                        Applying writes these changes to the project immediately.
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="timp-modal-foot">
                        <button
                            className="timp-btn"
                            type="button"
                            onClick={() => setConfirmOpen(false)}
                        >
                            Cancel
                        </button>
                        <button
                            className="timp-btn primary"
                            type="button"
                            onClick={handleApply}
                            disabled={applyLoading}
                        >
                            {applyLoading ? (
                                <span className="timp-spin" style={{ width: 15, height: 15 }} />
                            ) : (
                                <Upload size={16} />
                            )}
                            {applyLoading ? "Applying…" : "Apply changes"}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ── State F: Success modal ────────────────────────────────────────────
    const renderSuccessModal = () => {
        if (!comparisonResponse) return null;
        const sum = comparisonResponse.projectTasksComparison.summary;
        return (
            <div className={`timp-modal-bg${successOpen ? " open" : ""}`}>
                <div className="timp-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="timp-modal-head">
                        <span className="timp-mi success"><CheckCircle size={21} /></span>
                        <div>
                            <h3>Import applied</h3>
                            <div className="timp-modal-ms">
                                Changes have been written to the project.
                            </div>
                        </div>
                    </div>
                    <div className="timp-modal-body">
                        <div className="timp-confirm-counts">
                            <div className="timp-cc-stat added">
                                <div className="v">{sum.additions}</div>
                                <div className="l">Added</div>
                            </div>
                            <div className="timp-cc-stat removed">
                                <div className="v">{sum.removals}</div>
                                <div className="l">Removed</div>
                            </div>
                            <div className="timp-cc-stat updated">
                                <div className="v">{sum.updates}</div>
                                <div className="l">Updated</div>
                            </div>
                        </div>
                    </div>
                    <div className="timp-modal-foot">
                        <button
                            className="timp-btn primary"
                            type="button"
                            onClick={handleFinishSuccess}
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ── Toasts ────────────────────────────────────────────────────────────
    const renderToasts = () => (
        <div className="timp-toast-wrap">
            {toasts.map((t) => (
                <div
                    key={t.id}
                    className={`timp-toast ${t.kind}${t.out ? " out" : ""}`}
                >
                    {t.kind === "ok"
                        ? <CheckCircle size={18} />
                        : <AlertTriangle size={18} />}
                    {/* msg may contain <b> for emphasis — controlled internal content only */}
                    <span
                        className="timp-t-msg"
                        dangerouslySetInnerHTML={{ __html: t.msg }}
                    />
                </div>
            ))}
        </div>
    );

    // ── Root render ───────────────────────────────────────────────────────
    const renderPage = () => {
        switch (pageState) {
            case "empty":   return renderEmpty();
            case "loading": return renderLoading();
            case "diff":    return renderDiff();
            case "error":   return renderError();
            default:        return renderEmpty();
        }
    };

    return (
        <PageWrapper title="Task Import" backgroundColor="#f4f5f7">
            <div className="timp">
                <div className="timp-page">
                    {/* Page header strip — always visible */}
                    <div className="timp-imp-head">
                        <span className="timp-ih-logo">
                            <FileInput size={23} />
                        </span>
                        <div className="timp-ih-main">
                            <h1 className="timp-ih-title">Task Import</h1>
                            <div className="timp-ih-sub">
                                Drop a piece-work CSV to preview and apply task changes.
                            </div>
                        </div>
                    </div>
                    {renderPage()}
                </div>
                {renderConfirmModal()}
                {renderSuccessModal()}
                {renderToasts()}
            </div>
        </PageWrapper>
    );
};

export default PieceImport;
